import { useState, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Chip, ChipList } from './Primitives.js';

/**
 * A list of short values, entered one at a time or pasted in bulk.
 *
 * For prefixes and numbers, where a textarea of one-per-line hides two faults a
 * person cannot see: a duplicate, and a value that is not what it looks like
 * (a space inside, a stray +). Each value becomes a chip the moment it is
 * accepted, a refused one says why beside the input, and a pasted list of forty
 * prefixes becomes forty chips rather than one wrong line.
 */
export function ChipInput({
  values, onChange, validate, format = (v) => v, placeholder, mono = false, id, label, disabled = false,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  /** Normalise and check one value: return it cleaned, or an error to show. */
  validate: (raw: string) => { value: string } | { error: string };
  /** How a kept value is shown, where that differs from how it is kept. */
  format?: (value: string) => string;
  placeholder?: string;
  mono?: boolean;
  id?: string;
  /** For assistive technology when there is no visible label. */
  label?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  /** Accepts every value in `text`, keeping order, dropping duplicates. */
  const accept = (text: string) => {
    const parts = text.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...values];
    const refused: string[] = [];
    for (const part of parts) {
      const r = validate(part);
      if ('error' in r) { refused.push(`${part}: ${r.error}`); continue; }
      if (!next.includes(r.value)) next.push(r.value);
    }
    if (next.length !== values.length) onChange(next);
    setError(refused.length > 0 ? refused.slice(0, 3).join(' · ') : null);
    setDraft(refused.length === 1 && parts.length === 1 ? draft : '');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      accept(draft);
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (/[\s,;]/.test(text.trim())) {
      e.preventDefault();
      accept(text);
    }
  };

  return (
    <div className="arc-stack" style={{ gap: 6 }}>
      {values.length > 0 && (
        <ChipList>
          {values.map((v) => (
            <Chip key={v} mono={mono}
              {...(!disabled ? { onRemove: () => onChange(values.filter((x) => x !== v)), removeLabel: `Remove ${format(v)}` } : {})}>
              {format(v)}
            </Chip>
          ))}
        </ChipList>
      )}
      {!disabled && (
        <div className="arc-row" style={{ gap: 6 }}>
          <input id={id} className={`arc-input${mono ? ' arc-mono' : ''}`} style={{ maxWidth: 220 }}
            value={draft} placeholder={placeholder} aria-label={label}
            onChange={(e) => { setDraft(e.target.value); setError(null); }}
            onKeyDown={onKey} onPaste={onPaste}
            onBlur={() => { if (draft.trim()) accept(draft); }} />
          <button type="button" className="arc-btn arc-btn-sm" disabled={!draft.trim()}
            onClick={() => accept(draft)}>
            Add
          </button>
        </div>
      )}
      {error && <p className="arc-error" style={{ margin: 0 }}>{error}</p>}
    </div>
  );
}
